import { getVueCompositionApi } from "../utils/vue-compat";
import FingerprintJS from "@fingerprintjs/fingerprintjs";
import { v4 as uuidv4 } from "uuid";
import { createApiClient } from "../utils/api-client";
import { KpiTrackerConfig, UserData, SessionData } from "../types";
import { UseKpiTrackerReturn } from "./useKpiTracker";

// Type definitions
type Ref<T> = any;

export interface UseSessionReturn {
  fingerprint: Ref<string | null>;
  userData: Ref<UserData | null>;
  getFingerprintAndInitializeSession: (campaignId: string) => Promise<void>;
  findSession: (campaignId: string) => void;
  createUpdateSession: (
    identifier: string,
    campaignId: string
  ) => Promise<void>;
  updateSessionProgress: (
    data: any,
    progressOverride?: { step: number | string; choosen_names?: string[] },
    markSessionAsFinished?: boolean
  ) => Promise<boolean>;
  updateSessionProgressOnUnload: (
    data: any,
    progressOverride?: { step: number | string; choosen_names?: string[] },
    markSessionAsFinished?: boolean
  ) => void;
  resetSessionId: () => void;
}

export function useSession(
  config: KpiTrackerConfig,
  kpiTracker: UseKpiTrackerReturn
): UseSessionReturn {
  const vue = getVueCompositionApi();
  const { ref, computed } = vue;

  const fingerprint = ref(null as string | null) as any;
  const userData = ref(null as UserData | null) as any;
  const campaignId = ref(null as string | null) as any;
  const gameStartedCount = ref(0) as any; // Track game_started_count from session response
  const apiClient = createApiClient(config);

  const currentGameId = computed(() => config.gameId);

  const sessionStorageKey =
    config.sessionStorageKey || `GAME_SESSION_${config.gameId}`;

  async function getFingerprintAndInitializeSession(campaignId: string) {
    try {
      console.log(
        "[Session] getFingerprintAndInitializeSession called with campaignId:",
        campaignId
      );
      const fp = await FingerprintJS.load();
      const result = await fp.get();

      if (result) {
        // Only store fingerprint data in userData.value, NOT KPI data
        // KPI data (confirmed_consent, voucher_redeemed, etc.) goes on top level
        userData.value = {
          screen_resolution: result.components.screenResolution,
          platform: result.components.platform,
          browser_languages: result.components.languages,
          timezone: result.components.timezone,
          browser_vendor: result.components.vendor,
        };

        console.log(
          "[Session] Fingerprint obtained, finding/creating session..."
        );
        await findSession(campaignId);
        console.log(
          "[Session] Session find/create completed, fingerprint:",
          fingerprint.value
        );
      }
    } catch (error) {
      console.error("[Session] Error getting fingerprint:", error);
    }
  }

  async function findSession(campaignId: string) {
    if (!currentGameId.value) {
      console.log("[Session] No game id retrieved. Cannot track.");
      return;
    }

    const uuid = uuidv4();
    const sessionId = localStorage.getItem(sessionStorageKey);

    if (!sessionId) {
      localStorage.setItem(sessionStorageKey, uuid);
      fingerprint.value = uuid;
      console.log("[Session] Creating new session with fingerprint:", uuid);
    } else {
      fingerprint.value = sessionId;
      console.log(
        "[Session] Found existing session with fingerprint:",
        sessionId
      );
    }

    if (fingerprint.value !== null) {
      console.log("[Session] Creating/updating session...");
      // Always try to get campaignId from URL first, then fall back to passed parameter
      let finalCampaignId = "";
      if (typeof window !== "undefined") {
        const urlCampaignId = new URLSearchParams(window.location.search).get("campaignId");
        if (urlCampaignId) {
          finalCampaignId = urlCampaignId;
          console.log("[Session] Using campaignId from URL:", finalCampaignId);
        } else if (campaignId) {
          finalCampaignId = campaignId;
          console.log("[Session] Using campaignId from parameter:", finalCampaignId);
        }
      } else if (campaignId) {
        finalCampaignId = campaignId;
        console.log("[Session] Using campaignId from parameter (no window):", finalCampaignId);
      }
      
      const response = await createUpdateSession(
        fingerprint.value,
        finalCampaignId
      );
      console.log("[Session] Session created/updated with campaignId:", finalCampaignId);

      // Persistent values are already restored in createUpdateSession from the response
    }
  }

  function resetSessionId() {
    localStorage.removeItem(sessionStorageKey);
    fingerprint.value = null;
    campaignId.value = null;
  }

  async function createUpdateSession(
    identifier: string,
    campaignIdParam: string
  ): Promise<any> {
    try {
      // Get campaignId - prioritize URL parameter, then use passed parameter
      let finalCampaignId = campaignIdParam;
      if (!finalCampaignId && typeof window !== "undefined") {
        const urlCampaignId = new URLSearchParams(window.location.search).get("campaignId");
        if (urlCampaignId) {
          finalCampaignId = urlCampaignId;
          console.log("[Session] createUpdateSession - Using campaignId from URL:", finalCampaignId);
        }
      }
      
      // Store campaignId for later use
      campaignId.value = finalCampaignId;

      const kpiData = kpiTracker.getKpiData();

      // Get current progress value from kpiTracker and convert to string
      // Progress values: 0 = "opened_game", 1 = "age_gate_clicked", 2 = "game_started", 3 = "game_finished"
      let currentProgressValue = 0;
      if (
        kpiTracker.progress &&
        kpiTracker.progress.value !== undefined &&
        kpiTracker.progress.value !== null
      ) {
        currentProgressValue = Number(kpiTracker.progress.value);
      }

      // Ensure it's a valid number (0, 1, 2, or 3)
      if (
        isNaN(currentProgressValue) ||
        currentProgressValue < 0 ||
        currentProgressValue > 3
      ) {
        console.warn(
          "[Session] createUpdateSession - Invalid progress value, resetting to 0:",
          currentProgressValue
        );
        currentProgressValue = 0;
        kpiTracker.setProgress(0);
      }

      // Convert number to string step name
      const progressStepNames: { [key: number]: string } = {
        0: "opened_game",
        1: "age_gate_clicked",
        2: "game_started",
        3: "game_finished",
      };
      const currentProgressStep =
        progressStepNames[currentProgressValue] || "opened_game";

      console.log(
        "[Session] createUpdateSession - Using progress value:",
        currentProgressValue,
        "->",
        currentProgressStep,
        "(from kpiTracker)"
      );

      console.log("[Session] createUpdateSession - Determined progress:", {
        fromKpiTracker: kpiTracker.progress?.value,
        finalProgress: currentProgressStep,
      });

      // Extract values that should be on top level (not in user_data)
      // NOTE: selected_person and times_played are NOT sent at all
      // Ensure all numeric values are actual numbers (not null/undefined) to prevent backend errors
      const topLevelFields: any = {
        confirmed_consent: kpiData.confirmed_consent ?? false,
        entered_crm_data: kpiData.entered_crm_data ?? false,
        game_play_time_seconds: Number(kpiData.game_play_time_seconds) || 0,
        voucher_redeemed: kpiData.voucher_redeemed ?? false,
        // selected_person and times_played are intentionally excluded
      };

      // Note: game_started_count and has_finished_game are handled via progress override
      // in updateSessionProgress, not in createUpdateSession

      // Build user_data with only fingerprint data (exclude KPI fields)
      let cleanUserData: any = {};
      if (userData.value) {
        // Only include fingerprint-related fields in user_data
        if (userData.value.screen_resolution)
          cleanUserData.screen_resolution = userData.value.screen_resolution;
        if (userData.value.platform)
          cleanUserData.platform = userData.value.platform;
        if (userData.value.browser_languages)
          cleanUserData.browser_languages = userData.value.browser_languages;
        if (userData.value.timezone)
          cleanUserData.timezone = userData.value.timezone;
        if (userData.value.browser_vendor)
          cleanUserData.browser_vendor = userData.value.browser_vendor;

        // Preserve sticky boolean values from existing user_data if they were true
        if (userData.value.confirmed_consent === true) {
          topLevelFields.confirmed_consent = true;
        }
        if (userData.value.voucher_redeemed === true) {
          topLevelFields.voucher_redeemed = true;
        }
      } else {
        // If no userData yet, we'll get it from fingerprint later
        // For now, just use empty object
      }

      // When creating a session, send top-level fields and clean user_data
      const sessionData: SessionData = {
        identifier: identifier,
        campaign_id: finalCampaignId,
        user_data: cleanUserData,
        ...topLevelFields,
        progress: { step: currentProgressStep, choosen_names: [] }, // Progress on top level as JSON object with string step name
      };

      console.log("[Session] createUpdateSession - Sending payload:", {
        identifier,
        campaign_id: finalCampaignId,
        progress: sessionData.progress,
        game_started_count: (sessionData as any).game_started_count,
        has_finished_game: (sessionData as any).has_finished_game,
        user_data_progress: sessionData.user_data.progress,
        full_payload: JSON.stringify(sessionData, null, 2),
      });

      const res = await apiClient.post(
        `public/games/${currentGameId.value}/sessions`,
        sessionData
      );

      console.log("Session created/updated:", res);

      // Extract and restore persistent values from response if session already existed
      if (res && res.data) {
        let needsUpdate = false;

        // Restore game_started_count from response
        if (res.data.game_started_count !== undefined) {
          gameStartedCount.value = Number(res.data.game_started_count) || 0;
          console.log(
            "[Session] Restored game_started_count:",
            gameStartedCount.value
          );
        }

        // Restore has_finished_game from response (if it was already true, keep it true)
        // Note: has_finished_game is now handled via mark_session_as_finished parameter

        // Restore values from top-level fields (these are the "sticky" values that stay true once set)
        if (
          res.data.confirmed_consent === true &&
          !kpiTracker.confirmedConsent.value
        ) {
          kpiTracker.setConfirmedConsent(true);
          console.log(
            "[Session] Restored confirmed_consent: true (from top-level)"
          );
          needsUpdate = true;
        }
        if (
          res.data.voucher_redeemed === true &&
          !kpiTracker.voucherRedeemed.value
        ) {
          kpiTracker.setVoucherRedeemed(true);
          console.log(
            "[Session] Restored voucher_redeemed: true (from top-level)"
          );
          needsUpdate = true;
        }

        // Also check user_data as fallback
        if (res.data.user_data) {
          const responseUserData = res.data.user_data;

          // Restore confirmed_consent if it was true in user_data
          if (
            responseUserData.confirmed_consent === true &&
            !kpiTracker.confirmedConsent.value
          ) {
            kpiTracker.setConfirmedConsent(true);
            console.log(
              "[Session] Restored confirmed_consent: true (from user_data)"
            );
            needsUpdate = true;
          }

          // Restore voucher_redeemed if it was true in user_data
          if (
            responseUserData.voucher_redeemed === true &&
            !kpiTracker.voucherRedeemed.value
          ) {
            kpiTracker.setVoucherRedeemed(true);
            console.log(
              "[Session] Restored voucher_redeemed: true (from user_data)"
            );
            needsUpdate = true;
          }

          // Update userData.value with ONLY fingerprint data from response (exclude KPI fields)
          // KPI fields (confirmed_consent, voucher_redeemed, etc.) should NOT be in user_data
          if (userData.value) {
            // Only preserve fingerprint-related fields, exclude all KPI fields
            userData.value = {
              screen_resolution:
                userData.value.screen_resolution ||
                responseUserData.screen_resolution,
              platform: userData.value.platform || responseUserData.platform,
              browser_languages:
                userData.value.browser_languages ||
                responseUserData.browser_languages,
              timezone: userData.value.timezone || responseUserData.timezone,
              browser_vendor:
                userData.value.browser_vendor ||
                responseUserData.browser_vendor,
            };
          } else {
            // Only store fingerprint data, not KPI data
            userData.value = {
              screen_resolution: responseUserData.screen_resolution,
              platform: responseUserData.platform,
              browser_languages: responseUserData.browser_languages,
              timezone: responseUserData.timezone,
              browser_vendor: responseUserData.browser_vendor,
            };
          }

          // Restore progress from user_data or progress array
          // Progress step names: "opened_game" = 0, "age_gate_clicked" = 1, "game_started" = 2, "game_finished" = 3
          let existingProgress = null;
          if (responseUserData.progress !== undefined) {
            existingProgress = Number(responseUserData.progress);
          } else if (
            res.data.progress &&
            Array.isArray(res.data.progress) &&
            res.data.progress.length > 0
          ) {
            const latestProgress =
              res.data.progress[res.data.progress.length - 1];
            if (
              latestProgress &&
              latestProgress.progress &&
              latestProgress.progress.step !== undefined
            ) {
              const stepValue = latestProgress.progress.step;
              // Handle both number and string steps
              if (typeof stepValue === "string") {
                const stepNameToNumber: { [key: string]: number } = {
                  opened_game: 0,
                  age_gate_clicked: 1,
                  game_started: 2,
                  game_finished: 3,
                };
                existingProgress =
                  stepNameToNumber[stepValue] !== undefined
                    ? stepNameToNumber[stepValue]
                    : null;
              } else {
                existingProgress = Number(stepValue);
              }
            }
          }

          if (
            existingProgress !== null &&
            !isNaN(existingProgress) &&
            existingProgress >= 0 &&
            existingProgress <= 3
          ) {
            // Only restore progress if it's higher than current (don't go backwards)
            const currentProgress = kpiTracker.progress?.value ?? 0;
            if (existingProgress > currentProgress) {
              kpiTracker.setProgress(existingProgress);
              console.log(
                "[Session] Restored progress from session:",
                existingProgress
              );
              needsUpdate = true;
            }
          }
        }

        // If we restored values, send an update to ensure they're persisted
        if (needsUpdate && fingerprint.value) {
          console.log(
            "[Session] Sending update after restoring values from response"
          );
          await updateSessionProgress(kpiTracker.getKpiData());
        }
      }

      return res;
    } catch (error) {
      console.error("Error creating/updating session:", error);
    }
  }

  async function updateSessionProgress(
    data: any,
    progressOverride?: { step: number | string; choosen_names?: string[] },
    markSessionAsFinished?: boolean
  ): Promise<boolean> {
    console.log("[Session] updateSessionProgress called", {
      hasFingerprint: !!fingerprint.value,
      hasGameId: !!currentGameId.value,
      progress: kpiTracker.progress?.value,
    });

    if (!currentGameId.value) {
      console.log("[Session] No game id retrieved. Cannot track.");
      return Promise.resolve(false);
    }

    // If session is not initialized yet, initialize it first
    if (!fingerprint.value) {
      console.log("[Session] No fingerprint found, initializing session...");
      try {
        const urlParams =
          typeof window !== "undefined"
            ? new URLSearchParams(window.location.search)
            : null;
        const campaignIdParam = urlParams?.get("campaignId") ?? "";

        // Try to initialize even if campaignId is empty (it can be set later)
        // Use empty string if not available - the API might accept it
        console.log(
          "[Session] Initializing session with campaignId:",
          campaignIdParam || "(empty - will try to get from URL later)"
        );
        await getFingerprintAndInitializeSession(campaignIdParam || "");
        // Wait a bit for the session to be created - check multiple times
        let retries = 0;
        while (!fingerprint.value && retries < 10) {
          await new Promise((resolve) => setTimeout(resolve, 100));
          retries++;
        }

        if (!fingerprint.value) {
          console.error(
            "[Session] Session initialization failed - no fingerprint after retries"
          );
          return Promise.resolve(false);
        }
        console.log(
          "[Session] Session initialized successfully, fingerprint:",
          fingerprint.value
        );
      } catch (error) {
        console.error("[Session] Error initializing session:", error);
        return Promise.resolve(false);
      }
    }

    if (!fingerprint.value) {
      console.log(
        "[Session] Fatal: No session retrieved after initialization. Cannot track."
      );
      return Promise.resolve(false);
    }

    if (!campaignId.value) {
      console.log(
        "Warning: No campaign ID available. Cannot include in payload."
      );
    }

    try {
      // Determine progress value - use override if provided, otherwise use kpiTracker value
      // Progress values: 0 = "opened_game", 1 = "age_gate_clicked", 2 = "game_started", 3 = "game_finished"
      let progressStep: number | string = "opened_game";
      let choosenNames: string[] = [];

      if (progressOverride && progressOverride.step !== undefined) {
        progressStep = progressOverride.step;
        choosenNames = progressOverride.choosen_names || [];
        console.log(
          "[Session] updateSessionProgress - Using progress override:",
          progressStep,
          "with choosen_names:",
          choosenNames
        );
      } else {
        // Get current progress value from kpiTracker and convert to string
        let progressValue = 0;
        if (
          kpiTracker.progress &&
          kpiTracker.progress.value !== undefined &&
          kpiTracker.progress.value !== null
        ) {
          progressValue = Number(kpiTracker.progress.value);
        } else if (
          data &&
          data.progress !== undefined &&
          data.progress !== null
        ) {
          progressValue = Number(data.progress);
        }

        // Ensure it's a valid number (0, 1, 2, or 3)
        if (isNaN(progressValue) || progressValue < 0 || progressValue > 3) {
          progressValue = 0;
        }

        // Convert number to string step name
        const progressStepNames: { [key: number]: string } = {
          0: "opened_game",
          1: "age_gate_clicked",
          2: "game_started",
          3: "game_finished",
        };
        progressStep = progressStepNames[progressValue] || "opened_game";
        choosenNames = [];
      }

      console.log("[Session] updateSessionProgress - Determined progress:", {
        fromKpiTracker: kpiTracker.progress?.value,
        fromData: data?.progress,
        progressOverride: progressOverride,
        finalProgress: progressStep,
        markSessionAsFinished: markSessionAsFinished,
      });

      // Build payload - extract top-level fields and clean user_data
      const kpiData = kpiTracker.getKpiData();

      // Extract values that should be on top level (not in user_data)
      // NOTE: selected_person and times_played are NOT sent at all
      // Ensure all numeric values are actual numbers (not null/undefined) to prevent backend errors
      const topLevelFields: any = {
        confirmed_consent: kpiData.confirmed_consent ?? false,
        entered_crm_data: kpiData.entered_crm_data ?? false,
        game_play_time_seconds: Number(kpiData.game_play_time_seconds) || 0,
        voucher_redeemed: kpiData.voucher_redeemed ?? false,
        // selected_person and times_played are intentionally excluded
      };

      // If markSessionAsFinished is true, send mark_session_as_finished parameter
      // This will set has_finished_game = true in the backend
      if (markSessionAsFinished === true) {
        topLevelFields.mark_session_as_finished = true;
        console.log(
          "[Session] updateSessionProgress - Marking session as finished (has_finished_game will be set to true by backend)"
        );
      }

      // Build clean user_data with only fingerprint data (exclude KPI fields)
      const userDataPayload: any = {};
      if (userData.value) {
        // Only include fingerprint-related fields in user_data
        if (userData.value.screen_resolution)
          userDataPayload.screen_resolution = userData.value.screen_resolution;
        if (userData.value.platform)
          userDataPayload.platform = userData.value.platform;
        if (userData.value.browser_languages)
          userDataPayload.browser_languages = userData.value.browser_languages;
        if (userData.value.timezone)
          userDataPayload.timezone = userData.value.timezone;
        if (userData.value.browser_vendor)
          userDataPayload.browser_vendor = userData.value.browser_vendor;

        // Preserve sticky boolean values from existing user_data if they were true
        if (userData.value.confirmed_consent === true) {
          topLevelFields.confirmed_consent = true;
        }
        if (userData.value.voucher_redeemed === true) {
          topLevelFields.voucher_redeemed = true;
        }
      }

      // Build payload structure
      // Top-level fields: confirmed_consent, entered_crm_data, game_play_time_seconds, voucher_redeemed, progress
      // user_data: only fingerprint data (screen_resolution, platform, browser_languages, timezone, browser_vendor)
      const payload: any = {
        identifier: fingerprint.value,
        campaign_id: campaignId.value || "",
        user_data: userDataPayload,
        ...topLevelFields,
        progress: { step: progressStep, choosen_names: choosenNames }, // Backend expects progress as JSON object: { "step": number | string, "choosen_names": string[] }
      };

      // If campaignId is not set, try to get it from URL as fallback
      if (!campaignId.value) {
        try {
          const urlParams = new URLSearchParams(window.location.search);
          const urlCampaignId = urlParams.get("campaignId");
          if (urlCampaignId) {
            payload.campaign_id = urlCampaignId;
            campaignId.value = urlCampaignId; // Store it for next time
          } else {
            payload.campaign_id = ""; // Set empty string if not available
          }
        } catch (e) {
          // Ignore if URL parsing fails
          payload.campaign_id = ""; // Set empty string if URL parsing fails
        }
      }

      // Merge any additional data from the input (in case there are other fields)
      if (data && typeof data === "object") {
        // Don't overwrite identifier, campaign_id, progress, or user_data
        Object.keys(data).forEach((key) => {
          if (
            key !== "identifier" &&
            key !== "campaign_id" &&
            key !== "progress" &&
            key !== "user_data"
          ) {
            // If it's not already in user_data, add it to user_data
            if (!payload.user_data[key]) {
              payload.user_data[key] = data[key];
            }
          }
        });
      }

      console.log("[Session] updateSessionProgress - Sending payload:", {
        url: `public/games/${currentGameId.value}/sessions/${fingerprint.value}`,
        progress: payload.progress,
        game_started_count: payload.game_started_count,
        has_finished_game: payload.has_finished_game,
        user_data_progress: payload.user_data?.progress,
        progressStep: progressStep,
        full_payload: JSON.stringify(payload, null, 2),
      });

      const res = await apiClient.put(
        `public/games/${currentGameId.value}/sessions/${fingerprint.value}`,
        payload
      );

      console.log("[Session] updateSessionProgress - Response:", res);

      // Extract game_started_count and has_finished_game from response to keep them in sync
      if (res && res.data) {
        if (res.data.game_started_count !== undefined) {
          gameStartedCount.value = Number(res.data.game_started_count) || 0;
          console.log(
            "[Session] Updated game_started_count from response:",
            gameStartedCount.value
          );
        }
        if (res.data.has_finished_game === true) {
          console.log("[Session] has_finished_game is true in response");
        }
      }

      if (res) {
        console.log("[Session] Session progress updated successfully");
        return Promise.resolve(true);
      } else {
        console.warn("[Session] Session update returned no response");
        return Promise.resolve(false);
      }
    } catch (error) {
      console.error("[Session] Error updating session progress:", error);
    }

    return Promise.resolve(false);
  }

  // Function to update session on page unload using sendBeacon or fetch with keepalive
  // This ensures the request is sent even when the page is closing
  function updateSessionProgressOnUnload(
    data: any,
    progressOverride?: { step: number | string; choosen_names?: string[] },
    markSessionAsFinished?: boolean
  ): void {
    if (!fingerprint.value || !currentGameId.value) {
      console.log(
        "[Session] updateSessionProgressOnUnload - No fingerprint or gameId, skipping"
      );
      return;
    }

    try {
      // Determine progress value - use override if provided, otherwise use kpiTracker value
      // Progress values: 0 = "opened_game", 1 = "age_gate_clicked", 2 = "game_started", 3 = "game_finished"
      let progressStep: number | string = "opened_game";
      let choosenNames: string[] = [];

      if (progressOverride && progressOverride.step !== undefined) {
        progressStep = progressOverride.step;
        choosenNames = progressOverride.choosen_names || [];
      } else {
        // Get current progress value from kpiTracker and convert to string
        let progressValue = 0;
        if (
          kpiTracker.progress &&
          kpiTracker.progress.value !== undefined &&
          kpiTracker.progress.value !== null
        ) {
          progressValue = Number(kpiTracker.progress.value);
        } else if (
          data &&
          data.progress !== undefined &&
          data.progress !== null
        ) {
          progressValue = Number(data.progress);
        }

        // Ensure it's a valid number (0, 1, 2, or 3)
        if (isNaN(progressValue) || progressValue < 0 || progressValue > 3) {
          progressValue = 0;
        }

        // Convert number to string step name
        const progressStepNames: { [key: number]: string } = {
          0: "opened_game",
          1: "age_gate_clicked",
          2: "game_started",
          3: "game_finished",
        };
        progressStep = progressStepNames[progressValue] || "opened_game";
        choosenNames = [];
      }

      // Build payload
      const kpiData = kpiTracker.getKpiData();
      // Ensure all numeric values are actual numbers (not null/undefined) to prevent backend errors
      const topLevelFields: any = {
        confirmed_consent: kpiData.confirmed_consent ?? false,
        entered_crm_data: kpiData.entered_crm_data ?? false,
        game_play_time_seconds: Number(kpiData.game_play_time_seconds) || 0,
        voucher_redeemed: kpiData.voucher_redeemed ?? false,
      };

      if (markSessionAsFinished === true) {
        topLevelFields.mark_session_as_finished = true;
      }

      // Build clean user_data with only fingerprint data
      const userDataPayload: any = {};
      if (userData.value) {
        if (userData.value.screen_resolution)
          userDataPayload.screen_resolution = userData.value.screen_resolution;
        if (userData.value.platform)
          userDataPayload.platform = userData.value.platform;
        if (userData.value.browser_languages)
          userDataPayload.browser_languages = userData.value.browser_languages;
        if (userData.value.timezone)
          userDataPayload.timezone = userData.value.timezone;
        if (userData.value.browser_vendor)
          userDataPayload.browser_vendor = userData.value.browser_vendor;
      }

      // Get campaignId - try from stored value first, then from URL
      let finalCampaignId = campaignId.value || "";
      if (!finalCampaignId && typeof window !== "undefined") {
        try {
          const urlParams = new URLSearchParams(window.location.search);
          const urlCampaignId = urlParams.get("campaignId");
          if (urlCampaignId) {
            finalCampaignId = urlCampaignId;
            campaignId.value = urlCampaignId; // Store it for consistency
          }
        } catch (e) {
          // Ignore if URL parsing fails
        }
      }

      const payload: any = {
        identifier: fingerprint.value,
        campaign_id: finalCampaignId,
        user_data: userDataPayload,
        ...topLevelFields,
        progress: { step: progressStep, choosen_names: choosenNames },
      };

      const url = `${config.apiUrl}/public/games/${currentGameId.value}/sessions/${fingerprint.value}`;
      const payloadString = JSON.stringify(payload);

      // Try to use sendBeacon first (most reliable for page unload)
      if (navigator.sendBeacon) {
        const blob = new Blob([payloadString], { type: "application/json" });
        const success = navigator.sendBeacon(url, blob);
        if (success) {
          console.log(
            "[Session] updateSessionProgressOnUnload - Sent via sendBeacon"
          );
          return;
        }
      }

      // Fallback to fetch with keepalive (works in most modern browsers)
      if (typeof fetch !== "undefined") {
        fetch(url, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            ...(config.apiUsername && config.apiPassword
              ? {
                  Authorization: `Basic ${btoa(
                    `${config.apiUsername}:${config.apiPassword}`
                  )}`,
                }
              : {}),
          },
          body: payloadString,
          keepalive: true, // This ensures the request continues even after page unload
        }).catch((error) => {
          console.log(
            "[Session] updateSessionProgressOnUnload - Fetch error (ignored):",
            error
          );
        });
        console.log(
          "[Session] updateSessionProgressOnUnload - Sent via fetch with keepalive"
        );
      }
    } catch (error) {
      console.log(
        "[Session] updateSessionProgressOnUnload - Error (ignored):",
        error
      );
    }
  }

  return {
    fingerprint,
    userData,
    getFingerprintAndInitializeSession,
    findSession,
    createUpdateSession,
    updateSessionProgress,
    updateSessionProgressOnUnload,
    resetSessionId,
  };
}
